import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { toast } from "react-toastify";

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)", // Background color
  width: "44px", // Width
  height: "44px", // Height
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "sno",
  },
  {
    id: 2,
    label: "Request No ",
    value: "requestNo",
  },
  {
    id: 3,
    label: "Return Status",
    value: "returnStatus",
  },
  {
    id: 4,
    label: "Item Name",
    value: "itemName",
  },
  {
    id: 5,
    label: "Store",
    value: "store",
  },
  {
    id: 6,
    label: "Patient UHID",
    value: "patientUhid",
  },
  {
    id: 7,
    label: "Patient Name",
    value: "patientName",
  },
];

// Proper dropdown options
const templateOptions = [
  { label: "All Return Records", value: "all_return_records" },
  { label: "Pending Returns", value: "pending_returns" },
  { label: "Approved Returns", value: "approved_returns" },
  { label: "Completed Returns", value: "completed_returns" },
  { label: "Rejected Returns", value: "rejected_returns" },
];

const storeOptions = [
  { label: "Main Warehouse", value: "main_warehouse" },
  { label: "Central Supply", value: "central_supply" },
  { label: "Emergency Stock", value: "emergency_stock" },
  { label: "Pharmacy", value: "pharmacy" },
  { label: "Surgical Store", value: "surgical_store" },
  { label: "Laboratory Store", value: "laboratory_store" },
];

const returnStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Completed", value: "completed" },
  { label: "Rejected", value: "rejected" },
  { label: "Cancelled", value: "cancelled" },
];

const itemNameOptions = [
  { label: "Medicines", value: "medicines" },
  { label: "Surgical Items", value: "surgical_items" },
  { label: "Medical Equipment", value: "medical_equipment" },
  { label: "Consumables", value: "consumables" },
  { label: "Laboratory Items", value: "laboratory_items" },
  { label: "Disposables", value: "disposables" },
];

const patientUHIDOptions = [
  { label: "KSG-24-0000022", value: "KSG-24-0000022" },
  { label: "KSG-24-0000023", value: "KSG-24-0000023" },
  { label: "KSG-24-0000024", value: "KSG-24-0000024" },
  { label: "KSG-24-0000025", value: "KSG-24-0000025" },
  { label: "KSG-24-0000026", value: "KSG-24-0000026" },
];

export default function ReportsPatientIndentLedgerReturn() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    searchTemplate: null,
    returnRequestedDate: null,
    store: null,
    returnStatus: null,
    itemName: null,
    patientUhid: null,
  });

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors

  // Download menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    sno: true,
    requestNo: true,
    returnStatus: true,
    itemName: true,
    store: true,
    patientUhid: true,
    patientName: true,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Patient Indent Return Ledger Item Wise',
        text: 'Patient Indent Return Ledger Item Wise Report',
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle menu item clicks
  const handleMenuClick = (item) => {
    handleClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        toast.info('PDF export feature coming soon');
        break;
      case 'Report Mail Send Scheduler':
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.returnRequestedDate) {
      errorFields.push('returnRequestedDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    setIsLoading(true);
    
    try {
      // Simulate API call - replace with actual API endpoint
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock data for demonstration - replace with actual API response
      const mockData = [
        {
          _id: "1",
          sno: "1",
          requestNo: "REQ1001",
          returnStatus: "Pending",
          itemName: "Surgical Masks",
          store: "Main Warehouse",
          patientUhid: "KSG-24-0000022",
          patientName: "John Doe"
        },
        {
          _id: "2",
          sno: "2",
          requestNo: "REQ1002",
          returnStatus: "Approved",
          itemName: "Syringes",
          store: "Central Supply",
          patientUhid: "KSG-24-0000023",
          patientName: "Jane Smith"
        },
        {
          _id: "3",
          sno: "3",
          requestNo: "REQ1003",
          returnStatus: "Completed",
          itemName: "Blood Pressure Monitor",
          store: "Emergency Stock",
          patientUhid: "KSG-24-0000024",
          patientName: "Michael Johnson"
        },
        {
          _id: "4",
          sno: "4",
          requestNo: "REQ1004",
          returnStatus: "Rejected",
          itemName: "IV Fluids",
          store: "Pharmacy",
          patientUhid: "KSG-24-0000025",
          patientName: "Emily Davis"
        },
        {
          _id: "5",
          sno: "5",
          requestNo: "REQ1005",
          returnStatus: "Pending",
          itemName: "ECG Electrodes",
          store: "Laboratory Store",
          patientUhid: "KSG-24-0000026",
          patientName: "Robert Brown"
        }
      ];
      
      setTableData(mockData);
      toast.success("Report data loaded successfully");
    } catch (error) {
      console.error("Error fetching report data:", error);
      toast.error("Failed to load report data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (tableData.length === 0) {
      toast.warning("Please search for data before exporting");
      return;
    }
    
    setIsExporting(true);
    
    try {
      // Simulate export process - replace with actual export logic
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success("Report exported successfully");
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              Patient Indent Return Ledger Item Wise
            </Typography>
          </Box>
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => handleInputChange('searchTemplate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  name="returnRequestedDate"
                  label="Return Requested Date"
                  value={formData.returnRequestedDate}
                  onChange={(newValue) => {
                    handleInputChange('returnRequestedDate', newValue);
                    // Clear error when field is changed
                    if (errors.includes('returnRequestedDate')) {
                      setErrors(errors.filter(e => e !== 'returnRequestedDate'));
                    }
                  }}
                  slotProps={{
                    textField: { 
                      required: true, 
                      fullWidth: true,
                      InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                    },
                  }}
                  sx={{ 
                    bgcolor: "white",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: errors?.includes('returnRequestedDate') ? '1px solid red' : '1px solid inherit',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={storeOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.store}
                  onChange={(event, newValue) => handleInputChange('store', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Store"
                      name="store"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={returnStatusOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.returnStatus}
                  onChange={(event, newValue) => handleInputChange('returnStatus', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Return Status"
                      name="returnStatus"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={itemNameOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.itemName}
                  onChange={(event, newValue) => handleInputChange('itemName', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item Name"
                      name="itemName"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={patientUHIDOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.patientUhid}
                  onChange={(event, newValue) => handleInputChange('patientUhid', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Patient UHID"
                      name="patientUhid"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting || tableData.length === 0}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              my: "20px",
              p: "20px",
            }}
          >
              {/* dynamic button */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pb: "15px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                  >
                    Rows: {tableData?.length || 0} | Columns: {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon
                        sx={{
                          cursor: "pointer",
                          color: "transparent",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleShare}>
                      <ShareIcon 
                        sx={{
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps}>
                      <IconButton onClick={handleClick}>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <TableContainer component={Paper}>
                <Table aria-label="patient indent return ledger table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {headLabel?.map((option) => (
                        visibleColumns[option?.value] && (
                          <TableCell
                            key={option?.id}
                            sx={{ textAlign: option?.align || "left" }}
                          >
                            {option?.label}
                          </TableCell>
                        )
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !Array.isArray(tableData) || tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((row) => (
                        <TableRow key={row?._id}>
                          {headLabel?.map((column, index) => (
                            visibleColumns[column?.value] && (
                              <TableCell key={column?.id}>
                                {row?.[column?.value]}
                              </TableCell>
                            )
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "250px",
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <React.Fragment key={item}>
                    <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                      {item}
                    </MenuItem>
                    {index !== menuItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Menu>
              
              {/* Filter Menu for Column Visibility */}
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={handleFilterClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "200px",
                  },
                }}
              >
                <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                  Show/Hide Columns
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => toggleColumn('sno')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>SNO</Box>
                    {visibleColumns.sno && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('requestNo')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Request No</Box>
                    {visibleColumns.requestNo && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('returnStatus')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Return Status</Box>
                    {visibleColumns.returnStatus && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('itemName')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Item Name</Box>
                    {visibleColumns.itemName && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('store')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Store</Box>
                    {visibleColumns.store && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientUhid')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient UHID</Box>
                    {visibleColumns.patientUhid && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientName')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient Name</Box>
                    {visibleColumns.patientName && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
              </Menu>
            </Box>
        </Box>
      </Box>
    </Container>
  );
}
